import {computed, EventEmitter, Injectable} from '@angular/core';
import {CartService} from "./cart.service";
import {CartCreateForm} from "../models/cart.model";
import Swal from "sweetalert2";
import {createLogger} from "../../shared/helpers/logger/logger";
import {GuestCartService} from "../../shared/helpers/guest-cart/guest-cart.service";
import {UserService} from "./user.service";
import {AnalyticsGaService} from "../../shared/helpers/analytics/analytics-ga.service";

const logger = createLogger('CartManagerService');

@Injectable({
    providedIn: 'root'
})
export class CartManagerService {
    public productAdded = new EventEmitter<void>();
    isLoggedIn = computed(() => this.userService.isLoggedIn());

    constructor(
        private cartService: CartService,
        private guestService: GuestCartService,
        private userService: UserService,
        private analyticsService: AnalyticsGaService
    ) {
    }

    async addEntityToCart(
        entityType: 'Product' | 'Course' | 'Event',
        entityUuid: string,
        madeToOrder: number,
        quantity = 1,
        variantUuid?: string
    ) {
        if (entityType === 'Course' || entityType === 'Event') {
            quantity = 1;
            try {
                const cartParams: any = {expand: 'cartItems,cartItems.entity,cartItems.variant'};
                if (!this.isLoggedIn()) cartParams.guest_token = this.guestService.getGuestToken();
                const cartResponse = await this.cartService.getCart(cartParams);
                const cartItems = cartResponse.data?.cartItems || [];
                const isCourseInCart = cartItems.some(item => item.entity_type === 'Course' && item.entity.uuid === entityUuid);
                if (isCourseInCart) {
                    await Swal.fire({
                        title: 'Already in Cart',
                        text: 'You can only purchase one of each course.',
                        icon: 'info',
                        confirmButtonText: 'OK'
                    });
                    return;
                }
            } catch (error) {
                logger.error('Error checking cart contents:', error);
            }
        }

        const cartData: CartCreateForm = {
            entity_type: entityType,
            entity_uuid: entityUuid,
            quantity,
            made_to_order_products: madeToOrder > 0,
            guest_token: this.isLoggedIn() ? null : this.guestService.getGuestToken()
        };
        if (variantUuid) cartData.variant_uuid = variantUuid;

        try {
            await this.cartService.createCart(cartData);
            await Swal.fire({
                toast: true,
                position: 'bottom',
                icon: 'success',
                title: 'Item added to cart',
                showConfirmButton: false,
                timer: 3000
            });
            const cartResponse = await this.cartService.getCart({expand: 'cartItems,cartItems.entity,cartItems.variant'});
            const addedItem = cartResponse.data?.cartItems?.find(item => item.entity.uuid === entityUuid);
            if (addedItem) {
                this.analyticsService.addToCart(addedItem.entity, entityType, quantity);
            }
            this.productAdded.emit();
        } catch (error) {
            logger.error('Error adding to cart:', error);
            await Swal.fire({
                title: 'Error',
                text: 'Failed to add to cart. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    }
}
